import Vue from 'vue'
import Vuex from 'vuex'

function initialState() {
    // It's possible that initial values for the search filters could be passed in via the querystring.
    const queryString = new URLSearchParams(new URL(document.URL).search)

    const state = {
        mapOptions: {
            markerIcon: '/images/location_arrow.svg',

            styles: [
                {
                    'elementType': 'geometry',
                    'stylers': [
                        {
                            'color': '#f5f5f5'
                        }
                    ]
                },
                {
                    'elementType': 'labels.icon',
                    'stylers': [
                        {
                            'visibility': 'off'
                        }
                    ]
                },
                {
                    'elementType': 'labels.text.fill',
                    'stylers': [
                        {
                            'color': '#616161'
                        }
                    ]
                },
                {
                    'elementType': 'labels.text.stroke',
                    'stylers': [
                        {
                            'color': '#f5f5f5'
                        }
                    ]
                },
                {
                    'featureType': 'administrative.land_parcel',
                    'elementType': 'labels.text.fill',
                    'stylers': [
                        {
                            'color': '#bdbdbd'
                        }
                    ]
                },
                {
                    'featureType': 'poi',
                    'elementType': 'geometry',
                    'stylers': [
                        {
                            'color': '#eeeeee'
                        }
                    ]
                },
                {
                    'featureType': 'poi',
                    'elementType': 'labels.text.fill',
                    'stylers': [
                        {
                            'color': '#757575'
                        }
                    ]
                },
                {
                    'featureType': 'poi.park',
                    'elementType': 'geometry',
                    'stylers': [
                        {
                            'color': '#e5e5e5'
                        }
                    ]
                },
                {
                    'featureType': 'poi.park',
                    'elementType': 'labels.text.fill',
                    'stylers': [
                        {
                            'color': '#9e9e9e'
                        }
                    ]
                },
                {
                    'featureType': 'road',
                    'elementType': 'geometry',
                    'stylers': [
                        {
                            'color': '#ffffff'
                        }
                    ]
                },
                {
                    'featureType': 'road.arterial',
                    'elementType': 'labels.text.fill',
                    'stylers': [
                        {
                            'color': '#757575'
                        }
                    ]
                },
                {
                    'featureType': 'road.highway',
                    'elementType': 'geometry',
                    'stylers': [
                        {
                            'color': '#dadada'
                        }
                    ]
                },
                {
                    'featureType': 'road.highway',
                    'elementType': 'labels.text.fill',
                    'stylers': [
                        {
                            'color': '#616161'
                        }
                    ]
                },
                {
                    'featureType': 'road.local',
                    'elementType': 'labels.text.fill',
                    'stylers': [
                        {
                            'color': '#9e9e9e'
                        }
                    ]
                },
                {
                    'featureType': 'transit.line',
                    'elementType': 'geometry',
                    'stylers': [
                        {
                            'color': '#e5e5e5'
                        }
                    ]
                },
                {
                    'featureType': 'transit.station',
                    'elementType': 'geometry',
                    'stylers': [
                        {
                            'color': '#eeeeee'
                        }
                    ]
                },
                {
                    'featureType': 'water',
                    'elementType': 'geometry',
                    'stylers': [
                        {
                            'color': '#c9c9c9'
                        }
                    ]
                },
                {
                    'featureType': 'water',
                    'elementType': 'labels.text.fill',
                    'stylers': [
                        {
                            'color': '#9e9e9e'
                        }
                    ]
                }
            ]
        },

        search: {
            filters: {
                currentPageId: currentPageId,
                from: (queryString.has('from') && queryString.get('from')) || null,
                keywords: (queryString.has('keywords') && queryString.get('keywords')) || null,
                orderBy: (queryString.has('orderBy') && queryString.get('orderBy')) || null,
                page: (queryString.has('page') && queryString.get('page')) || 1,
                showAllResults: (queryString.has('showAllResults') && queryString.get('showAllResults')) || false,
                to: (queryString.has('to') && queryString.get('to')) || null
            },
            hasFilter: queryString.has('keywords') || queryString.has('from') || queryString.has('to') || Array.from(queryString.keys()).some(x => x.startsWith('filter_')),
            pageCount: 0,
            regions: null,
            resultCount: 0,
            resultsLoaded: 0,
            results: null
        },

        propertiesSearch: {
            currentTab: 'regions-list'
        },

        returnUrl: (queryString.has('returnUrl') && queryString.get('returnUrl')) || ''
    }

    // Search Filters are dynamic per page, so we have to check for these separately
    for (let i of queryString.keys()) {
        if (i.startsWith('filter_')) {
            state.search.filters[i] = queryString.getAll(i)
        }
    }

    return state
}

export function createStore() {
    return new Vuex.Store({
        actions: {
            addSearchFilter({ commit }, { filterName, initialValue }) {
                commit('addSearchFilter', { filterName: filterName, initialValue: initialValue })
            },

            removeSearchFilter({ commit }, { parent, value }) {
                commit('removeSearchFilter', { parent: parent, value: value })
            },

            resetSearch({ commit }) {
                commit('resetSearch')
            },

            resetSearchFilters({ commit }) {
                commit('resetSearchFilters')
            },

            resetSearchResults({ commit }) {
                commit('resetSearchResults')
            },

            updatePropertiesSearchCurrentTab({ commit }, tabName) {
                commit('updatePropertiesSearchCurrentTab', tabName)
            },

            updateSearchFilter({ getters, commit }, { key, value }) {
                commit('updateSearchFilter', { key, value })

                history.replaceState({}, '', new URL(document.URL).pathname + '?' + getters.searchFiltersQuerystring())
            },

            updateSearchPageCount({ commit }, pageCount) {
                commit('updateSearchPageCount', pageCount)
            },

            updateSearchRegions({ commit }, regions) {
                commit('updateSearchRegions', regions)
            },

            updateSearchResultCount({ commit }, resultCount) {
                commit('updateSearchResultCount', resultCount)
            },

            updateSearchResults({ commit, state }, results) {
                if (state.search.results) {
                    commit('updateSearchResults', Vue._.concat(state.search.results, results))
                } else {
                    commit('updateSearchResults', results)
                }
            },

            updateSearchResultsLoaded({ commit }, resultsLoaded) {
                commit('updateSearchResultsLoaded', resultsLoaded)
            }
        },
        getters: {
            returnUrl: (state, getters) => link => {
                const url = new URL(document.URL)
                return link.Url + (link.Url.indexOf('?')>-1?'&':'?') + 'returnUrl=' + encodeURIComponent(url.pathname + '?' + getters.searchFiltersQuerystring())
            },

            returnVirtualUrl: (state, getters) => link => {
                const url = new URL(document.URL)

                return link + '?returnUrl=' + url.pathname + '&' + getters.searchFiltersQuerystring() + '&isVirtual=true'
            },

            searchFiltersQuerystring: (state) => (from = null, to = null, showAllResults = null) => {
                const filters = state.search.filters
                const querystringArray = []

                Object.keys(filters).forEach(function (x) {
                    if (x.startsWith('filter_')) {
                        Object.keys(filters[x]).filter(y => y.length > 0).forEach(function (y) {
                            querystringArray.push(x + '=' + filters[x][y])
                        })
                    }
                    else if (x === 'showAllResults') {
                        if (showAllResults !== null) {
                            if (showAllResults) {
                                querystringArray.push(x + '=' + showAllResults)
                            }
                        } else {
                            querystringArray.push(x + '=' + filters['showAllResults'])
                        }
                    }
                    else {
                        if (x === 'from' && from) {
                            querystringArray.push(x + '=' + from)
                        } else if (x === 'to' && to) {
                            querystringArray.push(x + '=' + to)
                        } else if (filters[x]) {
                            querystringArray.push(x + '=' + filters[x])
                        }
                    }
                })

                const queryString = querystringArray.join('&')

                return queryString
            },

            moreResultsAvailable: (state) => {
                return state.search.resultsLoaded < state.search.resultCount
            }
        },
        state: initialState,
        mutations: {
            addSearchFilter(state, { filterName, initialValue }) {
                Vue.set(state.search.filters, filterName, initialValue)
            },

            clearFilters(state) {
                Object.keys(state.search.filters).forEach(function () {

                })
            },

            removeSearchFilter(state, { parent, value }) {
                state.search.filters[parent] = state.search.filters[parent].filter(function (val) { return val !== value })
            },

            resetSearch(state) {
                const theInitialState = initialState().search

                Object.keys(theInitialState).forEach(key => {
                    state[key] = theInitialState[key]
                })
            },

            resetSearchFilters(state) {
                Object.keys(state.search.filters).forEach(key => {
                    if (key.startsWith('filter_')) {
                        state.search.filters[key] = []
                    }
                })
            },

            resetSearchResults(state) {
                state.search.results = null
            },

            updatePropertiesSearchCurrentTab(state, tabName) {
                state.propertiesSearch.currentTab = tabName
            },

            updateSearchFilter(state, { key, value }) {
                state.search.filters[key] = value
            },

            updateSearchPageCount(state, pageCount) {
                state.search.pageCount = pageCount
            },

            updateSearchRegions(state, regions) {
                state.search.regions = regions
            },

            updateSearchResultCount(state, resultCount) {
                state.search.resultCount = resultCount
            },

            updateSearchResults(state, results) {
                state.search.results = results
            },

            updateSearchResultsLoaded(state, resultsLoaded) {
                if (resultsLoaded > state.search.resultCount) {
                    resultsLoaded = state.search.resultCount
                }
                state.search.resultsLoaded = resultsLoaded
                
            }
        }
    })
}